import { useEffect, useRef, useState } from 'react';
import { useLocale } from '../../application/i18n/LocaleProvider';
import { findLanguage } from '../../domain/i18n/languages';
import { ChevronDownIcon } from './BrandIcons';
import FlagDisc from './FlagIcons';
import LanguageSwitcher from './LanguageSwitcher';

/**
 * The header's trailing control: which language you are reading, and the way to
 * change it.
 *
 * It was a menu glyph — a set of sliders that opened a panel with the switcher
 * inside. The glyph was the whole problem: a four-locale product whose front door
 * gives no clue which locale you are standing in, behind an icon that could have
 * meant settings, filters or anything else. It now WEARS the answer, flag and
 * language code, so the state is legible before the panel is opened and the
 * affordance says what it does. A knowing divergence from the round icon button
 * in the approved composition (SPEC-001 PR-1.8).
 *
 * The panel stays a panel: three languages is too few to be worth a full sheet,
 * and it must not push the hero around when it opens.
 */
export default function LanguageMenu() {
    const { languageCode, t } = useLocale();
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);
    const language = findLanguage(languageCode);

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }

        const closeOnOutsideInteraction = (event) => {
            if (!containerRef.current?.contains(event.target)) {
                setIsOpen(false);
            }
        };

        const closeOnEscape = (event) => {
            if (event.key === 'Escape') {
                setIsOpen(false);
            }
        };

        document.addEventListener('pointerdown', closeOnOutsideInteraction);
        document.addEventListener('keydown', closeOnEscape);

        return () => {
            document.removeEventListener('pointerdown', closeOnOutsideInteraction);
            document.removeEventListener('keydown', closeOnEscape);
        };
    }, [isOpen]);

    return (
        <div className="language-menu" ref={containerRef}>
            <button
                type="button"
                className="language-menu__trigger"
                /* The visible label is a flag and two letters, which a screen
                   reader would announce as "DE" and nothing else. The accessible
                   name carries the same fact in words. */
                aria-label={t('language.current', { language: language.endonym })}
                aria-expanded={isOpen}
                aria-controls="language-menu-panel"
                onClick={() => setIsOpen((open) => !open)}
            >
                <FlagDisc nation={language.flagCode} size={20} />
                <span className="language-menu__code" aria-hidden="true">
                    {language.code.toUpperCase()}
                </span>
                <ChevronDownIcon className="language-menu__caret" size={16} />
            </button>

            {isOpen ? (
                <div className="language-menu__panel" id="language-menu-panel">
                    <LanguageSwitcher />
                </div>
            ) : null}
        </div>
    );
}
